import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Sidebar} from 'primereact/sidebar'
import {forwardRef, useImperativeHandle, useMemo} from 'react'
import {useParams} from 'react-router-dom'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {validEmail} from '../../utils/CommonUtils'
import {useAuth} from '../authentication/AuthProvider'
import CustomForm from '../form-utils/CustomForm'
import {formLayout, getComposedDataList} from '../form/Constant'
import FormHeader from '../form/FormHeader'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {nextOfKinList} from './NextOfKinConstant'
import {useNextOfKinCRUD} from './query-keys/NextOfKinKeysManager'

const NextOfKinForm = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const patientId = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])
    //#endregion

    //#region State
    const visible = useSignal(false)
    const data = useSignal({})
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const {mutateAsync: createOrUpdateNextOfKin} = useNextOfKinCRUD(
        !!data.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )
    //#endregion

    //#region Handle Function
    const handleShowForm = value => {
        data.value = value && Object.keys(value)?.length > 0 ? {...value} : {}
        visible.value = true
    }
    const handleHideForm = () => {
        data.value = {}
        visible.value = false
    }

    const handleOnSubmit = async callBackData => {
        if (callBackData.email && !validEmail(callBackData?.email)) {
            showNotification(severityNotification.error, 'Email is invalid !')
            return
        }
        let toPostData = {
            ...callBackData,
            id: data.value?.id ? data.value.id : null,
            addressId: data.value?.addressId ? data.value.addressId : null,
            userId: patientId,
        }

        const nextOfKinData = await createOrUpdateNextOfKin(toPostData)

        if (nextOfKinData?.id) {
            const {nextOfKinKeys} = QueryKeyManager(
                QueryKeyTypes.nextOfKin,
                user?.id,
            )

            //TODO: Temporary Refetch Table
            queryClient.invalidateQueries({
                queryKey: nextOfKinKeys.pattern(),
            })

            // try {
            //     reloadDataTable(QueryKey + patientId, nextOfKinData)
            // } catch (err) {
            //     console.log('Fail to reload table')
            // }

            handleHideForm()
        }
    }

    //#endregion

    //#region Memo
    const dataList = useMemo(() => {
        return getComposedDataList(nextOfKinList, data.value)
    }, [data.value])
    //#endregion

    return (
        <Sidebar
            className="p-sidebar-full "
            visible={visible.value}
            blockScroll
            onHide={handleHideForm}
            fullScreen>
            <FormHeader
                header={translateCommons('RELEVANT CONTACTS FORM')}
                onHide={handleHideForm}
            />
            <CustomForm
                onSubmit={handleOnSubmit}
                layout={formLayout.group}
                dataList={dataList}
            />
        </Sidebar>
    )
})

export default NextOfKinForm
